import * as moment from "moment-timezone";
import React, {
  useState,
  useMemo,
  useEffect
} from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import useOutSideItemClick from "src/library/hooks/useOutsideItemClick";
import { HtmlBody } from "src/library/components/Notes";
import { NotificationType } from "src/core/notifications/api/notifications.api";
import LoadingSpinner from "src/library/components/LoadingSpinner/LoadingSpinner";
import { useOrder } from "src/core/orders/hooks";
import { OrderOrigin } from "src/core/orders/api/orders.api";

import { useSmartNotifications } from "../hooks";
import { SmartGradientColor } from "./SmartGradientContainer";

export interface SmartNotificationProps {
  userId: number,
}

const SmartNotification: React.ReactFCWithChildren<SmartNotificationProps> = ({ userId }) => {
  const history = useHistory();
  const [ showContent, setShowContent ] = useState(false);
  const notifcationRef = React.useRef<HTMLDivElement>(null);
  const [ { currentOrder }, { createOrder } ] = useOrder();

  const {
    isLoading,
    showSmartNotification,
    currentNotification: smartNotification,
    handleMarkAsRead,
    handleDismissNotification
  } = useSmartNotifications(userId);

  useOutSideItemClick(notifcationRef, () => {
    setShowContent(false);
  });

  // make content is shown if notification changes
  useEffect(() => {
    if (smartNotification && !showContent) {
      setShowContent(true);
    }
  // disabled lint for deps as don't want to action when showContent
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ smartNotification ]);

  // The actions available based on the notification type property
  const smartNotificationActions: JSX.Element | null = useMemo(() => {
    if (smartNotification) {
      switch (smartNotification.type) {
        case NotificationType.ORDER_REMINDER: {
          return (
            <>
              <Button
                onClick={() => {
                  setShowContent(false);
                  handleMarkAsRead();
                  handleDismissNotification();
                  createOrder(moment.utc(smartNotification.data.orderDeliveryDate as string, "YYYY-MM-DD").toDate(), OrderOrigin.Reminder);
                }}
              >
                Create Order
              </Button>

              <Button
                className="secondary-action"
                $buttonType="plain"
                onClick={() => {
                  setShowContent(false);
                  handleMarkAsRead();
                  handleDismissNotification();
                  history.push("/orders");
                }}
              >
                View All Orders
              </Button>
            </>
          );
        }

        case NotificationType.CONCLUDING_LISTS:
        case NotificationType.SUPPORT_TICKET_UPDATE: {
          throw new Error(`${smartNotification.type} is an unhandled NotificationType`);
        }

        default: {
          throw new Error("A valid NotificationType must be used");
        }
      }
    } else {
      return null;
    }
  }, [
    smartNotification,
    handleDismissNotification,
    handleMarkAsRead,
    history
  ]);

  // don't display notification if in the context of an order
  if (!showSmartNotification || currentOrder.mode === "edit") {
    return null;
  }

  return (
    <SmartNotificationWrapper ref={notifcationRef}>
      {smartNotification && (
        <>
          <div className={`content-container ${showContent ? "content-in" : "content-out"}`}>
            <div className="left-accent" />

            {isLoading && (
              <LoadingSpinner
                className="loading-spinner"
                type="circular"
                size={20}
              />
            )}

            {!isLoading && (
              <>
                <div
                  className="header"
                  onClick={() => {
                    setShowContent(false);
                    handleMarkAsRead();
                    handleDismissNotification();
                  }}
                >
                  <Icon
                    size={14}
                    icon={Icons.CLOSE}
                    cursor="pointer"
                  />
                </div>

                <div className="title">
                  <Icon
                    size={14}
                    icon={Icons.ALARM}
                    $fillColor="smartIcon"
                  />

                  <Text
                    className="title-text"
                    fontSize="h3"
                  >
                    {smartNotification.title}
                  </Text>
                </div>

                <div className="content">
                  {smartNotification.body && <HtmlBody body={smartNotification.body} />}

                  <div className="actions">
                    {smartNotificationActions && smartNotificationActions}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div
        className="notification-button"
        onClick={() => setShowContent(!showContent)}
      >
        <Icon
          size={28}
          icon={Icons.BOLT}
          $fillColor="white"
          cursor="pointer"
        />
      </div>

    </SmartNotificationWrapper>
  );
};

const notificationHeight = "226px";
const boxShadow = "rgba(0, 0, 0, 0.2) 0px 18px 50px -10px";

const SmartNotificationWrapper = styled.div`
  ${({ theme }) => css`
    position: absolute;
    margin-left: 45px;
    right: 75px;
    bottom: 64px;
    z-index: 1000;

    .content-container {
      padding-left: 43px;
      padding-right: ${theme.spacing.padding.md};
      width: 554px;
      height: ${notificationHeight};
      background: white;
      border-radius: ${theme.borderRadius};
      box-shadow: ${boxShadow};
      transform: scale(0);
      transition: all 0.3s;
      transform-origin: bottom right;
    }

    .content-in {
      transform: scale(1);
    }
    
    .content-out {
      transform: scale(0);
    }

    .header {
      display: flex;
      justify-content: end;
      padding: ${theme.spacing.padding.sm}
    }

    .title {
      display: flex;
    }

    .title-text {
      margin-left: ${theme.spacing.margins.lg};
    }

    .title svg {
      align-self: center;
    }

    .content {
      margin-left: 14px;
      padding: ${theme.spacing.padding.lg};
    }
    
    .left-accent {
      position: absolute;
      width: 7px;
      height: ${notificationHeight};
      left: 0;
      border-radius: ${theme.borderRadius} 0 0 ${theme.borderRadius};
      background: ${SmartGradientColor};
    }

    .actions {
      display: flex;
      margin-top: ${theme.spacing.margins.lg}
    }

    .secondary-action {
      margin-left: ${theme.spacing.margins.lg}
    }

    .notification-button {
      position: absolute;
      bottom: -18px;
      right: -18px;
      height: 36px;
      width: 36px;
      background: ${SmartGradientColor};
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: ${boxShadow};
    }

    .notification-button svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .notification-button:hover {
      cursor: pointer;
    }
  `}
`;

export default SmartNotification;
